import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';

import { createCatalogDrugApi, listCatalogFormOptionsApi, listDrugCodeMappingsApi } from 'api/drug';
import {
  drugCodeAllowedValueRegex,
  drugNameAllowedCharRegex,
  drugNameAllowedCharsHint,
  drugNameAllowedValueRegex,
} from 'common/constants/regex';
import { getGraphQLErrorMessage } from 'common/utils/i18nErrorTranslations';

import { DRUG_CODE_FORMAT_HINT, listCatalogSubmitBlockers } from '../utils/catalogSubmitBlockers';
import {
  describeManualProductIdConflict,
  handleManualProductIdError,
  resolveManualProductId,
  toManualProductIdInput,
} from '../utils/manualProductId';
import CatalogSubmitBlockersHint from './CatalogSubmitBlockersHint';
import ManualProductIdField, { ManualProductIdInstructions } from './ManualProductIdField';
import {
  isDrugCodeTaken,
  resolveDrugCodeForName,
  toDrugCodeIndex,
} from '../utils/drugCodeGenerator';

// Form aliases and their pack size units both come from listCatalogFormOptions
// now. They used to be two hardcoded arrays here that had to be kept in step by
// hand with e-scribe-ui and smartphos-core -- three places for one list.

// Long enough that a drug name typed at speed resolves once rather than once per
// keystroke. No network call is involved -- the registry is already local.
const DRUG_CODE_DEBOUNCE_MS = 400;

const AddCatalogDialog = ({ open, onClose, onCreated }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [drugName, setDrugName] = useState('');
  const [strength, setStrength] = useState('');
  const [formAlias, setFormAlias] = useState('');
  const [drugCode, setDrugCode] = useState('');
  const [packSizeValue, setPackSizeValue] = useState('');
  const [packSizeUnit, setPackSizeUnit] = useState('');
  const [compounding, setCompounding] = useState(false);
  // POM-6099 — relay eligibility (Diamond via Quello) is a second routing flag, independent of
  // compounding eligibility (central fill): a drug can carry either, both or neither.
  const [relay, setRelay] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formOptions, setFormOptions] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);
  const [formsError, setFormsError] = useState(false);
  const [codeIndex, setCodeIndex] = useState(() => toDrugCodeIndex([]));
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [codeManual, setCodeManual] = useState(false);
  // True while the code on screen came from the registry rather than generation.
  const [codeFromRegistry, setCodeFromRegistry] = useState(false);
  // The registry's own spelling of the matched drug, for the helper text.
  const [mappedDrugName, setMappedDrugName] = useState('');
  // POM-6514: the Product ID field only appears once createCatalogDrug says it could not
  // generate one, so a successful automatic generation never asks for manual entry.
  // takenProductIds are the manual Product IDs the backend refused as already in the catalog.
  const [showManualProductId, setShowManualProductId] = useState(false);
  const [manualProductIdCode, setManualProductIdCode] = useState('');
  const [takenProductIds, setTakenProductIds] = useState([]);

  useEffect(() => {
    if (!open) return;

    setShowManualProductId(false);
    setManualProductIdCode('');
    setTakenProductIds([]);
    setDrugName('');
    setStrength('');
    setFormAlias('');
    setDrugCode('');
    setPackSizeValue('');
    setPackSizeUnit('');
    setCompounding(false);
    setRelay(false);
    setSubmitted(false);
    setSubmitError('');
    setCodeManual(false);
    setCodeFromRegistry(false);
    setMappedDrugName('');
    setCodeIndex(toDrugCodeIndex([]));
    setFormOptions([]);
    setFormsError(false);
    setLoadingCodes(true);
    setLoadingForms(true);

    listCatalogFormOptionsApi()
      .then(setFormOptions)
      .catch(() => {
        // Unlike the drug code registry there is no manual fallback: a form the
        // backend does not know is refused on submit, so an empty list has to
        // read as an error the user can retry rather than an empty dropdown.
        setFormsError(true);
      })
      .finally(() => setLoadingForms(false));

    listDrugCodeMappingsApi()
      .then((mappings) => setCodeIndex(toDrugCodeIndex(mappings)))
      .catch(() => {
        // Without the registry there is nothing to look a code up in or check
        // against, so fall back to the manual entry this field used to require
        // rather than blocking the create outright. The backend still refuses a
        // code that contradicts the mapping, so a hand-typed one is safe.
        setCodeManual(true);
      })
      .finally(() => setLoadingCodes(false));
  }, [open]);

  // Every user-facing list is sorted case-insensitively rather than left in the
  // order the API returned it.
  const sortedFormAliases = useMemo(
    () =>
      formOptions
        .map(({ formAlias: alias }) => alias)
        .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' })),
    [formOptions]
  );

  const unitsForSelectedForm = useMemo(() => {
    const selected = formOptions.find(({ formAlias: alias }) => alias === formAlias);

    return [...(selected?.units ?? [])].sort((left, right) =>
      left.localeCompare(right, undefined, { sensitivity: 'base' })
    );
  }, [formOptions, formAlias]);

  const formShortCode =
    formOptions.find(({ formAlias: alias }) => alias === formAlias)?.shortCode ?? '';

  const handleFormAliasChange = (event) => {
    setFormAlias(event.target.value);
    // Units are scoped to the form, so one picked for the previous form may no
    // longer be offered. Clear it rather than submit a pair the catalog does not
    // recognise.
    setPackSizeUnit('');
  };

  // Resolves the code as the drug name settles: the registry's code if the drug
  // is already known, otherwise one derived from the name. Debounced.
  //
  // Unlike generation, a registry hit applies even after the user has taken the
  // field over via Change -- a registered drug's code is not theirs to change,
  // and the backend would reject a contradicting one anyway.
  useEffect(() => {
    if (!open || loadingCodes) return undefined;

    const trimmedName = drugName.trim();

    if (!trimmedName) {
      if (!codeManual) setDrugCode('');
      setCodeFromRegistry(false);
      setMappedDrugName('');
      return undefined;
    }

    const timer = setTimeout(() => {
      const {
        drugCode: resolvedCode,
        isExisting,
        canonicalDrugName,
      } = resolveDrugCodeForName(trimmedName, codeIndex);

      setCodeFromRegistry(isExisting);
      setMappedDrugName(canonicalDrugName);

      if (isExisting || !codeManual) setDrugCode(resolvedCode);
    }, DRUG_CODE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [open, drugName, codeManual, loadingCodes, codeIndex]);

  const trimmedDrugCode = drugCode.trim();
  const drugCodeValid = drugCodeAllowedValueRegex.test(trimmedDrugCode);

  // A registered drug's own code is in the taken set by definition, so it has to
  // be exempt here -- otherwise a drug already in the registry could never be
  // added to the catalog again. For anything else this is only ever true of a
  // hand-typed code: a generated one is picked precisely because it wasn't taken.
  const drugCodeTaken = !codeFromRegistry && isDrugCodeTaken(trimmedDrugCode, codeIndex.takenCodes);

  // A registered code is never editable: reassigning it by hand is exactly the
  // drift the registry exists to prevent. A generated one is read-only until
  // Change hands it to the user, and an empty code (a name the generator can't
  // derive from, e.g. "B12") stays editable so the create is never blocked.
  const codeLocked = codeFromRegistry || (!codeManual && Boolean(trimmedDrugCode));
  const isDrugCodeMalformed = Boolean(trimmedDrugCode) && !drugCodeValid;
  const drugCodeError = drugCodeTaken || isDrugCodeMalformed || (submitted && !drugCodeValid);

  const getDrugCodeHelperText = () => {
    if (drugCodeTaken) return `${trimmedDrugCode} is already used — pick another`;
    if (loadingCodes) return 'Checking existing codes…';
    if (isDrugCodeMalformed) return DRUG_CODE_FORMAT_HINT;
    if (codeFromRegistry) return `Existing code for ${mappedDrugName}`;
    if (codeLocked) return 'Auto-generated from drug name';
    return '3 characters, used in the Product ID (e.g. FLX)';
  };

  const drugNameValid = drugNameAllowedValueRegex.test(drugName);

  const manualProductVariant = {
    strength,
    formAlias,
    manualProductIdCode,
    needsManualProductId: showManualProductId,
  };
  const manualProductId = resolveManualProductId(manualProductVariant, formShortCode);
  const manualProductIdConflict = describeManualProductIdConflict({
    productIds: [manualProductId],
    takenProductIds,
    index: 0,
  });

  const submitBlockers = listCatalogSubmitBlockers({
    drugName,
    drugCode,
    isDrugCodeTaken: drugCodeTaken,
    isLoadingCodes: loadingCodes,
    variants: [{ ...manualProductVariant, manualProductIdConflict }],
  });
  const canSave = submitBlockers.length === 0;

  const handleClose = () => {
    setSubmitted(false);
    onClose();
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!canSave) return;
    setSaving(true);
    setSubmitError('');

    const trimmedDrugName = drugName.trim();

    const input = {
      drugName: trimmedDrugName,
      drugCode: drugCode.trim().toUpperCase(),
      variants: [
        {
          strength: strength.trim(),
          formAlias,
          isCompoundingEligible: compounding,
          isRelayEligible: relay,
          ...(packSizeValue.trim() ? { packSizeValue: packSizeValue.trim() } : {}),
          ...(packSizeUnit ? { packSizeUnit } : {}),
          ...(showManualProductId && toManualProductIdInput(manualProductIdCode, formShortCode)),
        },
      ],
    };

    createCatalogDrugApi(input)
      .then(() => {
        setSaving(false);
        enqueueSnackbar(`Catalog item "${trimmedDrugName}" created`, { variant: 'success' });
        onCreated();
        handleClose();
      })
      .catch((error) => {
        setSaving(false);
        // The one variant here is the one that failed, and the one manual Product ID the one
        // taken, whatever the error names.
        const args = {
          error,
          showManualProductId,
          onGenerationFailed: () => setShowManualProductId(true),
          onProductIdTaken: () => setTakenProductIds((prev) => [...prev, manualProductId]),
          enqueueSnackbar,
        };

        if (handleManualProductIdError(args)) return;

        // Surface the backend validation error inline; keep the form open and
        // editable so the user can correct the input and resubmit (POM-5739).
        setSubmitError(getGraphQLErrorMessage(error));
      });
  };

  // Block backdrop-click dismissal, and prevent any close (incl. Escape) while a
  // create is in flight — the only way out is the Cancel button (POM-5739).
  const handleDialogClose = (_, reason) => {
    if (saving || reason === 'backdropClick') return;
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Typography variant='h6' sx={{ fontWeight: 700 }}>
          Add to Catalog
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Create a new catalog drug identity that inventory items can link to.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {submitError && (
            <Alert severity='error' onClose={() => setSubmitError('')}>
              {submitError}
            </Alert>
          )}

          <TextField
            size='small'
            fullWidth
            label='Drug Name *'
            value={drugName}
            onChange={(e) => setDrugName(e.target.value)}
            inputProps={{
              onKeyDown: (e) => {
                if (e.key.length === 1 && !drugNameAllowedCharRegex.test(e.key)) {
                  e.preventDefault();
                }
              },
            }}
            error={(submitted && drugName.trim().length === 0) || !drugNameValid}
            helperText={
              drugNameValid
                ? 'Generic drug identity for the new catalog record.'
                : drugNameAllowedCharsHint
            }
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              size='small'
              fullWidth
              label='Strength *'
              placeholder='e.g. 250 mg'
              value={strength}
              onChange={(e) => setStrength(e.target.value)}
              error={submitted && strength.trim().length === 0}
            />
            <TextField
              select
              size='small'
              fullWidth
              label='Form *'
              value={formAlias}
              onChange={handleFormAliasChange}
              disabled={loadingForms || formsError}
              error={(submitted && formAlias.length === 0) || formsError}
              helperText={
                formsError ? 'Could not load the form list. Close and reopen to retry.' : ''
              }
            >
              {sortedFormAliases.map((alias) => (
                <MenuItem key={alias} value={alias}>
                  {alias}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              size='small'
              fullWidth
              label='Pack Size Value'
              placeholder='e.g. 20'
              value={packSizeValue}
              onChange={(e) => setPackSizeValue(e.target.value)}
            />
            <TextField
              select
              size='small'
              fullWidth
              label='Pack Size Unit'
              value={packSizeUnit}
              onChange={(e) => setPackSizeUnit(e.target.value)}
              disabled={unitsForSelectedForm.length === 0}
              helperText={
                formAlias && unitsForSelectedForm.length === 0
                  ? 'No pack size unit is recorded for this form yet.'
                  : ''
              }
            >
              <MenuItem value=''>
                <em>None</em>
              </MenuItem>
              {unitsForSelectedForm.map((unit) => (
                <MenuItem key={unit} value={unit}>
                  {unit}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <TextField
              size='small'
              label='Drug Code *'
              value={drugCode}
              disabled={codeLocked || loadingCodes}
              onChange={(e) => {
                if (codeFromRegistry) return;
                setCodeManual(true);
                setDrugCode(e.target.value.toUpperCase().slice(0, 3));
              }}
              error={drugCodeError}
              // inputProps is nested here rather than passed alongside as its own
              // prop: jsx-no-duplicate-props compares case-insensitively, so
              // inputProps + InputProps as siblings reads as a duplicate.
              InputProps={{
                inputProps: {
                  maxLength: 3,
                  style: { textTransform: 'uppercase', letterSpacing: 4 },
                },
                // Sits inside the field rather than beside it. The input itself is
                // disabled while locked, but an adornment button is a sibling of
                // the input rather than a child, so it stays clickable.
                //
                // Absent for a registry code: that one is locked outright, so
                // offering an edit affordance would only promise something the
                // backend goes on to reject.
                endAdornment:
                  codeLocked && !codeFromRegistry ? (
                    <InputAdornment position='end'>
                      <Tooltip title='Edit drug code'>
                        <IconButton
                          size='small'
                          aria-label='Edit drug code'
                          onClick={() => setCodeManual(true)}
                        >
                          <EditIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ) : null,
              }}
              sx={{ width: 160, flexShrink: 0 }}
            />
            <Typography variant='caption' color={drugCodeError ? 'error' : 'text.secondary'}>
              {getDrugCodeHelperText()}
            </Typography>
          </Box>

          {showManualProductId && (
            <>
              <ManualProductIdInstructions conflictText={manualProductIdConflict} />
              <ManualProductIdField
                id='AddCatalogDialog-productId-input'
                size='small'
                value={manualProductIdCode}
                onChange={setManualProductIdCode}
                shortCode={formShortCode}
                formSelected={Boolean(formAlias)}
                drugCode={trimmedDrugCode.toUpperCase()}
                autoFocus
                conflictText={manualProductIdConflict}
              />
            </>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1, rowGap: 0.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size='small'
                  checked={compounding}
                  onChange={(e) => setCompounding(e.target.checked)}
                />
              }
              label={<Typography variant='body2'>Compounding eligible</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size='small'
                  checked={relay}
                  onChange={(e) => setRelay(e.target.checked)}
                />
              }
              label={<Typography variant='body2'>Relay eligible</Typography>}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <CatalogSubmitBlockersHint blockers={submitBlockers} />
        <Button onClick={handleClose} color='inherit' disabled={saving}>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleSave} disabled={!canSave || saving}>
          {saving ? 'Creating…' : 'Create Catalog Item'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddCatalogDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
};
AddCatalogDialog.defaultProps = {
  onCreated: () => {},
};

export default AddCatalogDialog;
